import { Grid } from "@mui/material";
import {
  CustomTable,
  FilterGroupComponent,
  InputOptionComponent,
  OverflowText,
  StackedBarChart,
  HorizontalNoGridBarChart,
  Title,
} from "components";
import {
  useGetCompanyCounts,
  useGetCompanyWatchlist,
  useGetCompanyWatchlistCriteria,
} from "./service/InternalDashboardService";
import { useCallback, useMemo, useState } from "react";
import { formatNumber } from "utils/formatNumber";
import { formatEllipsis } from "utils/formatString";
import { useLocation, useNavigate } from "react-router-dom";
import {
  generateUrlWithParams,
  paramParsify,
  paramStringify,
} from "utils/queryParams";
import { useGetCompanySectors } from "services/company";
import dayjs from "dayjs";

const InternalDashboardPage = () => {
  const location = useLocation();
  const queryParams = paramParsify(location?.search);
  const navigate = useNavigate();

  const [showWatchlistCriteria, setShowWatchlistCriteria] = useState(false);

  const { response: sectorsResp } = useGetCompanySectors();
  const { response: clientsBySectorsResp } = useGetCompanyCounts({
    by: "sector",
  });
  const { response: clientsByIndustryResp } = useGetCompanyCounts({
    by: "industry",
    enableParams: true,
  });
  const { response: clientsByCategoryResp } = useGetCompanyCounts({
    by: "category",
  });
  const { response: watchlistResp } = useGetCompanyWatchlist();
  const { response: watchlistCriteriaResp } = useGetCompanyWatchlistCriteria();

  const clientsBySector = useMemo(
    () =>
      clientsBySectorsResp?.data?.map((el) => ({
        label: `${formatNumber.toAmount(
          el?.total_count,
        )} (${el?.total_percentage?.toFixed(2)}%)`,
        value: el?.total_count,
        leftLabel: formatEllipsis(el?.name),
        leftFullLabel: el?.name,
      })),
    [clientsBySectorsResp?.data],
  );

  const clientsByCategory = useMemo(
    () =>
      clientsByCategoryResp?.data?.map((el) => ({
        label: el?.name,
        value: formatNumber.toAmount(el?.total_count),
      })) ?? [],
    [clientsByCategoryResp?.data],
  );

  const sectorsOption = useMemo(
    () =>
      sectorsResp?.data?.map((el) => ({
        label: el?.name,
        value: String(el?.id),
      })),
    [sectorsResp],
  );

  const clientsByIndustry = useMemo(
    () =>
      clientsByIndustryResp?.data?.map((el) => ({
        label: el?.name,
        value: formatNumber.toAmount(el?.total_count),
      })),
    [clientsByIndustryResp],
  );

  const watchlistHeadCells = [
    {
      id: "effect_code",
      label: <p className='text-xs'>Stock Code</p>,
    },
    {
      id: "name",
      label: <p className='text-xs'>Company Name</p>,
    },
    {
      id: "date",
      label: <p className='text-xs'>Date</p>,
    },
    {
      id: "criteria",
      label: <p className='text-xs'>Criteria</p>,
    },
  ];
  const watchlistBodyCells = useMemo(
    () =>
      watchlistResp?.data?.watchlist?.map((el, idx) => ({
        ...el,
        effect_code: <p className='text-sm'>{el?.effect_code}</p>,
        name: <OverflowText text={el?.name} textClassName='text-sm' />,
        date: (
          <p className='text-sm'>
            {el?.date ? dayjs(el?.date)?.format("DD MMMM YYYY") : "-"}
          </p>
        ),
        criteria: <p className='text-sm'>{String(el?.criteria) || "-"}</p>,
      })),
    [watchlistResp],
  );

  const watchlistCriteriaHeadCells = [
    {
      id: "id",
      label: <p className='text-[10px] text-[#5F737F]'>No</p>,
    },
    {
      id: "description",
      label: <p className='text-[10px] text-[#5F737F]'>Description</p>,
    },
  ];
  const watchlistCriteriaBodyCells = useMemo(
    () =>
      watchlistCriteriaResp?.data?.map((el) => ({
        ...el,
        no: <p className='text-xs'>{el?.id}</p>,
        description: <p className='text-xs'>{el?.description}</p>,
      })),
    [watchlistCriteriaResp],
  );

  const handleOnSelectSector = useCallback(
    (opt) => {
      const stringified = paramStringify({
        ...queryParams,
        sector_id: opt?.value,
      });
      navigate(generateUrlWithParams(location?.pathname, stringified));
    },
    [location?.pathname, navigate, queryParams],
  );

  const handleToggleWatchlistView = () => {
    setShowWatchlistCriteria((prev) => !prev);
  };

  return (
    <div className='internal-dashboard-page grid gap-4'>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <div className='section h-full'>
            <Title title='Clients by Sector' />
            {clientsBySector?.length && (
              <HorizontalNoGridBarChart
                id='clients-by-sector-chart'
                data={clientsBySector}
                height={clientsBySector?.length * 30}
              />
            )}
          </div>
        </Grid>
        <Grid item xs={6}>
          <div className='section h-full'>
            <Title title='Client Industries by Sectors' className='mb-4' />
            <div className='py-2'>
              <InputOptionComponent
                options={sectorsOption ?? []}
                value={sectorsOption?.find(
                  (el) => el?.value === (queryParams?.sector_id ?? "1"),
                )}
                onSelect={handleOnSelectSector}
              />
            </div>
            <StackedBarChart
              id='clients-industries-chart'
              dataBgColor='#556FB5'
              barThickness={40}
              borderRadius={0}
              data={clientsByIndustry}
            />
          </div>
        </Grid>
        <Grid item xs={6}>
          <div className='section h-full'>
            <div className='mb-5'>
              <Title title='Clients by Category' />
            </div>
            <StackedBarChart
              id='clients-categories-chart'
              barThickness={40}
              dataBgColor='#2DC48D'
              borderRadius={0}
              data={clientsByCategory}
              height={300}
            />
          </div>
        </Grid>
        <Grid item xs={6}>
          <div className='section h-full'>
            <div className='flex flex-row justify-between items-center mb-4'>
              <Title title='Watchlist Record Board' />
              <p
                onClick={handleToggleWatchlistView}
                className='text-sm text-[#556FB5] font-extrabold cursor-pointer'
              >
                {showWatchlistCriteria ? "Close" : "View Criteria"}
              </p>
            </div>
            {showWatchlistCriteria ? (
              <CustomTable
                headCells={watchlistCriteriaHeadCells}
                bodyCells={watchlistCriteriaBodyCells ?? []}
                borderless
                showPagination={false}
                maxHeight={420}
              />
            ) : (
              <div className='flex flex-col gap-4 py-2'>
                <div className='flex flex-row gap-4'>
                  <FilterGroupComponent
                    showStatusFilter={false}
                    showTypeFilter={false}
                    showDateRangePicker={false}
                    searchPlaceholder='Search by Company Name'
                    searchParamsQueryKey='name'
                  />
                </div>
                <CustomTable
                  headCells={watchlistHeadCells}
                  bodyCells={watchlistBodyCells ?? []}
                  emptyImgSrc='/images/empty-watchlist-record.svg'
                  total={watchlistResp?.data?.total ?? 0}
                  rowsPerPage={5}
                />
              </div>
            )}
          </div>
        </Grid>
      </Grid>
    </div>
  );
};

export default InternalDashboardPage;
